/**
 * LVS 77-3:2016 character pair spacing (a) at reference height h = 350 mm.
 *
 * Source: LVS 77-3:2016, Tables 9.4 - 9.8 (Font A) and 9.9 - 9.13 (Font B).
 *
 * Reading order (per spec "vispirms no augšas uz leju, pēc tam no kreisās uz
 * labo pusi"): outer key = LEFT character (row), inner key = RIGHT character
 * (column). The value `a` is the gap between adjacent character bounding
 * boxes (it does NOT include the character widths themselves).
 *
 * Latvian diacritic characters (Ā, Č, Ē, Ģ, Ī, Ķ, Ļ, Ņ, Š, Ū, Ž and lowercase
 * equivalents) inherit the spacing of their base Latin letter, per the
 * standard. Resolution of those should be handled at lookup time by mapping
 * the diacritic to its base letter before consulting these tables.
 *
 * All values are in millimetres at h = 350 mm. Negative values are
 * legitimate (close-fitting pairs such as L→T, P→A, T→A, etc.).
 *
 * Special-case digit→unit-abbreviation overrides per the notes following
 * tables 9.7 and 9.12 are provided as DIGIT_UNIT_A / DIGIT_UNIT_B.
 */

/** Font A — Table 9.4 — uppercase → uppercase + symbols ( /, -, ., ( ) */
export const KERN_A_UU: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    A: {
        A: 31,
        B: 50,
        C: 31,
        D: 50,
        E: 50,
        F: 50,
        G: 31,
        H: 50,
        I: 50,
        J: 25,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 31,
        P: 50,
        R: 50,
        S: 25,
        T: -19,
        U: 31,
        V: -19,
        Z: 38,
        Q: 31,
        W: -13,
        X: 31,
        Y: -19,
        '.': 44
    },
    B: {
        A: 31,
        B: 69,
        C: 50,
        D: 69,
        E: 69,
        F: 69,
        G: 50,
        H: 69,
        I: 69,
        J: 31,
        K: 69,
        L: 69,
        M: 69,
        N: 69,
        O: 50,
        P: 69,
        R: 69,
        S: 44,
        T: 25,
        U: 56,
        V: -25,
        Z: 50,
        Q: 50,
        W: 31,
        X: 31,
        Y: 25,
        '.': 44
    },
    C: {
        A: 25,
        B: 63,
        C: 50,
        D: 63,
        E: 63,
        F: 63,
        G: 50,
        H: 63,
        I: 63,
        J: 25,
        K: 63,
        L: 63,
        M: 63,
        N: 63,
        O: 50,
        P: 63,
        R: 63,
        S: 44,
        T: 19,
        U: 56,
        V: 19,
        Z: 44,
        Q: 50,
        W: 25,
        X: 25,
        Y: 19,
        '.': 44
    },
    D: {
        A: 31,
        B: 69,
        C: 50,
        D: 69,
        E: 69,
        F: 69,
        G: 50,
        H: 69,
        I: 69,
        J: 31,
        K: 69,
        L: 69,
        M: 69,
        N: 69,
        O: 56,
        P: 69,
        R: 69,
        S: 44,
        T: 25,
        U: 63,
        V: 25,
        Z: 44,
        Q: 56,
        W: 31,
        X: 31,
        Y: 25,
        '.': 44
    },
    E: {
        A: 38,
        B: 69,
        C: 50,
        D: 69,
        E: 69,
        F: 69,
        G: 50,
        H: 69,
        I: 69,
        J: 38,
        K: 69,
        L: 69,
        M: 69,
        N: 69,
        O: 50,
        P: 69,
        R: 69,
        S: 50,
        T: 38,
        U: 63,
        V: 38,
        Z: 56,
        Q: 50,
        W: 44,
        X: 38,
        Y: 38,
        '.': 56
    },
    F: {
        A: 0,
        B: 50,
        C: 38,
        D: 50,
        E: 50,
        F: 50,
        G: 38,
        H: 50,
        I: 50,
        J: -13,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 38,
        P: 50,
        R: 50,
        S: 38,
        T: 31,
        U: 44,
        V: 38,
        Z: 38,
        Q: 38,
        W: 38,
        X: 38,
        Y: 31,
        '.': 0
    },
    G: {
        A: 31,
        B: 69,
        C: 56,
        D: 69,
        E: 69,
        F: 69,
        G: 56,
        H: 69,
        I: 69,
        J: 31,
        K: 69,
        L: 69,
        M: 69,
        N: 69,
        O: 56,
        P: 69,
        R: 69,
        S: 44,
        T: 25,
        U: 63,
        V: 25,
        Z: 44,
        Q: 56,
        W: 31,
        X: 31,
        Y: 25,
        '.': 44
    },
    H: {
        A: 50,
        B: 81,
        C: 69,
        D: 81,
        E: 81,
        F: 81,
        G: 69,
        H: 81,
        I: 81,
        J: 44,
        K: 81,
        L: 81,
        M: 81,
        N: 81,
        O: 69,
        P: 81,
        R: 81,
        S: 56,
        T: 50,
        U: 75,
        V: 44,
        Z: 63,
        Q: 69,
        W: 50,
        X: 50,
        Y: 50,
        '.': 63
    },
    I: {
        A: 50,
        B: 81,
        C: 69,
        D: 81,
        E: 81,
        F: 81,
        G: 69,
        H: 81,
        I: 81,
        J: 44,
        K: 81,
        L: 81,
        M: 81,
        N: 81,
        O: 69,
        P: 81,
        R: 81,
        S: 56,
        T: 50,
        U: 75,
        V: 44,
        Z: 63,
        Q: 69,
        W: 50,
        X: 50,
        Y: 50,
        '.': 63
    },
    J: {
        A: 31,
        B: 75,
        C: 63,
        D: 75,
        E: 75,
        F: 75,
        G: 63,
        H: 75,
        I: 75,
        J: 31,
        K: 75,
        L: 75,
        M: 75,
        N: 75,
        O: 63,
        P: 75,
        R: 75,
        S: 44,
        T: 38,
        U: 69,
        V: 31,
        Z: 56,
        Q: 63,
        W: 38,
        X: 38,
        Y: 31,
        '.': 44
    },
    K: {
        A: 31,
        B: 50,
        C: 25,
        D: 50,
        E: 50,
        F: 50,
        G: 25,
        H: 50,
        I: 50,
        J: 25,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 25,
        P: 50,
        R: 50,
        S: 19,
        T: 25,
        U: 31,
        V: 19,
        Z: 44,
        Q: 25,
        W: 19,
        X: 25,
        Y: 19,
        '.': 44
    },
    L: {
        A: 25,
        B: 50,
        C: 31,
        D: 50,
        E: 50,
        F: 50,
        G: 31,
        H: 50,
        I: 50,
        J: 31,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 31,
        P: 50,
        R: 50,
        S: 31,
        T: -13,
        U: 31,
        V: -13,
        Z: 44,
        Q: 31,
        W: -6,
        X: 25,
        Y: -13,
        '.': 56
    },
    M: {
        A: 50,
        B: 81,
        C: 69,
        D: 81,
        E: 81,
        F: 81,
        G: 69,
        H: 81,
        I: 81,
        J: 44,
        K: 81,
        L: 81,
        M: 81,
        N: 81,
        O: 69,
        P: 81,
        R: 81,
        S: 56,
        T: 50,
        U: 75,
        V: 44,
        Z: 63,
        Q: 69,
        W: 50,
        X: 50,
        Y: 50,
        '.': 63
    },
    N: {
        A: 50,
        B: 81,
        C: 69,
        D: 81,
        E: 81,
        F: 81,
        G: 69,
        H: 81,
        I: 81,
        J: 44,
        K: 81,
        L: 81,
        M: 81,
        N: 81,
        O: 69,
        P: 81,
        R: 81,
        S: 56,
        T: 50,
        U: 75,
        V: 44,
        Z: 63,
        Q: 69,
        W: 50,
        X: 50,
        Y: 50,
        '.': 63
    },
    O: {
        A: 31,
        B: 69,
        C: 56,
        D: 69,
        E: 69,
        F: 69,
        G: 56,
        H: 69,
        I: 69,
        J: 31,
        K: 69,
        L: 69,
        M: 69,
        N: 69,
        O: 56,
        P: 69,
        R: 69,
        S: 44,
        T: 25,
        U: 63,
        V: 25,
        Z: 44,
        Q: 56,
        W: 31,
        X: 31,
        Y: 25,
        '.': 44
    },
    P: {
        A: -6,
        B: 56,
        C: 50,
        D: 56,
        E: 56,
        F: 56,
        G: 50,
        H: 56,
        I: 56,
        J: -19,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 50,
        P: 56,
        R: 56,
        S: 38,
        T: 25,
        U: 56,
        V: 25,
        Z: 44,
        Q: 50,
        W: 25,
        X: 19,
        Y: 25,
        '.': -6
    },
    R: {
        A: 31,
        B: 63,
        C: 38,
        D: 63,
        E: 63,
        F: 63,
        G: 38,
        H: 63,
        I: 63,
        J: 25,
        K: 63,
        L: 63,
        M: 63,
        N: 63,
        O: 38,
        P: 63,
        R: 63,
        S: 31,
        T: 13,
        U: 44,
        V: 13,
        Z: 38,
        Q: 38,
        W: 19,
        X: 31,
        Y: 13,
        '.': 44
    },
    S: {
        A: 25,
        B: 63,
        C: 50,
        D: 63,
        E: 63,
        F: 63,
        G: 50,
        H: 63,
        I: 63,
        J: 25,
        K: 63,
        L: 63,
        M: 63,
        N: 63,
        O: 50,
        P: 63,
        R: 63,
        S: 44,
        T: 25,
        U: 56,
        V: 25,
        Z: 44,
        Q: 50,
        W: 25,
        X: 31,
        Y: 25,
        '.': 44
    },
    T: {
        A: -19,
        B: 50,
        C: 25,
        D: 50,
        E: 50,
        F: 50,
        G: 25,
        H: 50,
        I: 50,
        J: -13,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 25,
        P: 50,
        R: 50,
        S: 19,
        T: 31,
        U: 38,
        V: 25,
        Z: 44,
        Q: 25,
        W: 31,
        X: 31,
        Y: 25,
        '.': 6
    },
    U: {
        A: 31,
        B: 75,
        C: 63,
        D: 75,
        E: 75,
        F: 75,
        G: 63,
        H: 75,
        I: 75,
        J: 31,
        K: 75,
        L: 75,
        M: 75,
        N: 75,
        O: 63,
        P: 75,
        R: 75,
        S: 44,
        T: 38,
        U: 69,
        V: 31,
        Z: 56,
        Q: 63,
        W: 38,
        X: 38,
        Y: 31,
        '.': 44
    },
    V: {
        A: -19,
        B: 44,
        C: 25,
        D: 44,
        E: 44,
        F: 44,
        G: 25,
        H: 44,
        I: 44,
        J: -13,
        K: 44,
        L: 44,
        M: 44,
        N: 44,
        O: 25,
        P: 44,
        R: 44,
        S: 13,
        T: 25,
        U: 31,
        V: 25,
        Z: 38,
        Q: 25,
        W: 25,
        X: 25,
        Y: 25,
        '.': 6
    },
    Z: {
        A: 31,
        B: 69,
        C: 50,
        D: 69,
        E: 69,
        F: 69,
        G: 50,
        H: 69,
        I: 69,
        J: 31,
        K: 69,
        L: 69,
        M: 69,
        N: 69,
        O: 50,
        P: 69,
        R: 69,
        S: 38,
        T: 38,
        U: 44,
        V: 38,
        Z: 56,
        Q: 50,
        W: 38,
        X: 31,
        Y: 38,
        '.': 56
    },
    Q: {
        A: 19,
        B: 50,
        C: 31,
        D: 50,
        E: 50,
        F: 50,
        G: 31,
        H: 50,
        I: 50,
        J: 25,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 31,
        P: 50,
        R: 50,
        S: 38,
        T: -6,
        U: 31,
        V: -6,
        Z: 38,
        Q: 31,
        W: 0,
        X: 19,
        Y: -6,
        '.': 44
    },
    W: {
        A: -13,
        B: 50,
        C: 31,
        D: 50,
        E: 50,
        F: 50,
        G: 31,
        H: 50,
        I: 50,
        J: -13,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 31,
        P: 50,
        R: 50,
        S: 19,
        T: 31,
        U: 38,
        V: 25,
        Z: 38,
        Q: 31,
        W: 25,
        X: 25,
        Y: 25,
        '.': 13
    },
    X: {
        A: 31,
        B: 50,
        C: 31,
        D: 50,
        E: 50,
        F: 50,
        G: 31,
        H: 50,
        I: 50,
        J: 25,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 31,
        P: 50,
        R: 50,
        S: 25,
        T: 31,
        U: 38,
        V: 25,
        Z: 44,
        Q: 31,
        W: 25,
        X: 31,
        Y: 25,
        '.': 44
    },
    Y: {
        A: -19,
        B: 50,
        C: 25,
        D: 50,
        E: 50,
        F: 50,
        G: 25,
        H: 50,
        I: 50,
        J: -13,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 25,
        P: 50,
        R: 50,
        S: 13,
        T: 25,
        U: 31,
        V: 25,
        Z: 38,
        Q: 25,
        W: 25,
        X: 25,
        Y: 25,
        '.': -13
    },
    '/': {
        A: -19,
        B: 44,
        C: 25,
        D: 44,
        E: 44,
        F: 44,
        G: 25,
        H: 44,
        I: 44,
        J: -13,
        K: 44,
        L: 44,
        M: 44,
        N: 44,
        O: 25,
        P: 44,
        R: 44,
        S: 13,
        T: 31,
        U: 31,
        V: 31,
        Z: 38,
        Q: 25,
        W: 31,
        X: 25,
        Y: 31
    },
    '-': {
        A: 50,
        B: 88,
        C: 75,
        D: 88,
        E: 88,
        F: 88,
        G: 75,
        H: 88,
        I: 88,
        J: 31,
        K: 88,
        L: 88,
        M: 88,
        N: 88,
        O: 88,
        P: 88,
        R: 88,
        S: 56,
        T: 25,
        U: 75,
        V: 38,
        Z: 63,
        Q: 75,
        W: 38,
        X: 38,
        Y: 31
    },
    '.': {
        A: 69,
        B: 81,
        C: 63,
        D: 81,
        E: 81,
        F: 81,
        G: 63,
        H: 81,
        I: 81,
        J: 69,
        K: 81,
        L: 81,
        M: 81,
        N: 81,
        O: 63,
        P: 81,
        R: 81,
        S: 69,
        T: 25,
        U: 63,
        V: 38,
        Z: 75,
        Q: 63,
        W: 38,
        X: 69,
        Y: 38
    },
    '(': {
        A: 38,
        B: 56,
        C: 38,
        D: 56,
        E: 56,
        F: 56,
        G: 38,
        H: 56,
        I: 56,
        J: 31,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 38,
        P: 56,
        R: 56,
        S: 31,
        T: 38,
        U: 44,
        V: 31,
        Z: 50,
        Q: 38,
        W: 31,
        X: 38,
        Y: 31
    }
}

/** Font A — Table 9.5 — uppercase → lowercase */
export const KERN_A_UL: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    A: {
        a: 31,
        b: 50,
        c: 31,
        d: 38,
        e: 31,
        f: 31,
        g: 38,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 44,
        m: 44,
        n: 44,
        o: 31,
        p: 44,
        r: 44,
        s: 25,
        t: 25,
        u: 38,
        v: 19,
        z: 31,
        q: 38,
        w: 19,
        x: 25,
        y: 19
    },
    B: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 56,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 56,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    C: {
        a: 44,
        b: 63,
        c: 56,
        d: 56,
        e: 56,
        f: 50,
        g: 56,
        h: 63,
        i: 63,
        j: 25,
        k: 63,
        l: 63,
        m: 63,
        n: 63,
        o: 56,
        p: 63,
        r: 63,
        s: 44,
        t: 50,
        u: 63,
        v: 44,
        z: 50,
        q: 56,
        w: 44,
        x: 44,
        y: 44
    },
    D: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    E: {
        a: 50,
        b: 69,
        c: 50,
        d: 56,
        e: 50,
        f: 50,
        g: 56,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 56,
        n: 56,
        o: 50,
        p: 56,
        r: 56,
        s: 50,
        t: 50,
        u: 56,
        v: 44,
        z: 50,
        q: 56,
        w: 44,
        x: 44,
        y: 44
    },
    F: {
        a: 19,
        b: 50,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 50,
        i: 50,
        j: 13,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 38,
        p: 50,
        r: 50,
        s: 25,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    G: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    H: {
        a: 63,
        b: 81,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 81,
        i: 81,
        j: 44,
        k: 81,
        l: 81,
        m: 75,
        n: 75,
        o: 69,
        p: 75,
        r: 81,
        s: 63,
        t: 69,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56
    },
    I: {
        a: 63,
        b: 81,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 81,
        i: 81,
        j: 44,
        k: 81,
        l: 81,
        m: 75,
        n: 75,
        o: 69,
        p: 75,
        r: 81,
        s: 63,
        t: 69,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56
    },
    J: {
        a: 56,
        b: 75,
        c: 56,
        d: 63,
        e: 63,
        f: 63,
        g: 63,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 75,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    K: {
        a: 25,
        b: 50,
        c: 25,
        d: 31,
        e: 25,
        f: 25,
        g: 31,
        h: 50,
        i: 44,
        j: 19,
        k: 50,
        l: 44,
        m: 44,
        n: 44,
        o: 25,
        p: 44,
        r: 38,
        s: 19,
        t: 19,
        u: 31,
        v: 13,
        z: 31,
        q: 31,
        w: 13,
        x: 25,
        y: 13
    },
    L: {
        a: 38,
        b: 50,
        c: 38,
        d: 38,
        e: 38,
        f: 25,
        g: 38,
        h: 50,
        i: 50,
        j: 19,
        k: 50,
        l: 44,
        m: 50,
        n: 50,
        o: 38,
        p: 50,
        r: 50,
        s: 31,
        t: 19,
        u: 38,
        v: 0,
        z: 44,
        q: 38,
        w: 0,
        x: 31,
        y: 6
    },
    M: {
        a: 63,
        b: 81,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 81,
        i: 81,
        j: 44,
        k: 81,
        l: 81,
        m: 75,
        n: 75,
        o: 69,
        p: 75,
        r: 81,
        s: 63,
        t: 69,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56
    },
    N: {
        a: 63,
        b: 81,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 81,
        i: 81,
        j: 44,
        k: 81,
        l: 81,
        m: 75,
        n: 75,
        o: 69,
        p: 75,
        r: 81,
        s: 63,
        t: 69,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56
    },
    O: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    P: {
        a: 31,
        b: 56,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 19,
        k: 56,
        l: 56,
        m: 56,
        n: 56,
        o: 38,
        p: 56,
        r: 56,
        s: 25,
        t: 44,
        u: 56,
        v: 38,
        z: 44,
        q: 44,
        w: 38,
        x: 38,
        y: 38
    },
    R: {
        a: 44,
        b: 63,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 63,
        i: 63,
        j: 31,
        k: 63,
        l: 63,
        m: 63,
        n: 63,
        o: 38,
        p: 63,
        r: 63,
        s: 44,
        t: 44,
        u: 50,
        v: 31,
        z: 44,
        q: 44,
        w: 31,
        x: 38,
        y: 31
    },
    S: {
        a: 44,
        b: 63,
        c: 50,
        d: 56,
        e: 56,
        f: 44,
        g: 56,
        h: 63,
        i: 63,
        j: 25,
        k: 63,
        l: 63,
        m: 63,
        n: 63,
        o: 50,
        p: 63,
        r: 63,
        s: 44,
        t: 56,
        u: 56,
        v: 31,
        z: 38,
        q: 56,
        w: 31,
        x: 31,
        y: 31
    },
    T: {
        a: 6,
        b: 50,
        c: 6,
        d: 13,
        e: 6,
        f: 25,
        g: 13,
        h: 50,
        i: 50,
        j: 13,
        k: 50,
        l: 50,
        m: 25,
        n: 25,
        o: 6,
        p: 25,
        r: 25,
        s: 6,
        t: 25,
        u: 25,
        v: 13,
        z: 13,
        q: 13,
        w: 13,
        x: 13,
        y: 13
    },
    U: {
        a: 56,
        b: 75,
        c: 56,
        d: 63,
        e: 63,
        f: 63,
        g: 63,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 75,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 69,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    V: {
        a: 13,
        b: 44,
        c: 19,
        d: 25,
        e: 19,
        f: 31,
        g: 25,
        h: 44,
        i: 44,
        j: 6,
        k: 44,
        l: 44,
        m: 38,
        n: 38,
        o: 19,
        p: 38,
        r: 38,
        s: 6,
        t: 31,
        u: 38,
        v: 19,
        z: 25,
        q: 25,
        w: 19,
        x: 19,
        y: 19
    },
    Z: {
        a: 50,
        b: 69,
        c: 50,
        d: 56,
        e: 50,
        f: 50,
        g: 56,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 56,
        n: 56,
        o: 50,
        p: 56,
        r: 56,
        s: 44,
        t: 56,
        u: 56,
        v: 44,
        z: 50,
        q: 56,
        w: 44,
        x: 44,
        y: 44
    },
    Q: {
        a: 38,
        b: 50,
        c: 31,
        d: 38,
        e: 31,
        f: 38,
        g: 38,
        h: 50,
        i: 50,
        j: 19,
        k: 50,
        l: 44,
        m: 50,
        n: 50,
        o: 31,
        p: 50,
        r: 50,
        s: 25,
        t: 38,
        u: 38,
        v: 25,
        z: 31,
        q: 38,
        w: 25,
        x: 25,
        y: 25
    },
    W: {
        a: 19,
        b: 50,
        c: 25,
        d: 31,
        e: 25,
        f: 31,
        g: 31,
        h: 50,
        i: 44,
        j: 6,
        k: 50,
        l: 44,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 13,
        t: 31,
        u: 38,
        v: 19,
        z: 25,
        q: 31,
        w: 19,
        x: 19,
        y: 19
    },
    X: {
        a: 25,
        b: 50,
        c: 25,
        d: 31,
        e: 25,
        f: 25,
        g: 31,
        h: 50,
        i: 44,
        j: 19,
        k: 50,
        l: 44,
        m: 44,
        n: 44,
        o: 25,
        p: 44,
        r: 38,
        s: 19,
        t: 19,
        u: 31,
        v: 13,
        z: 31,
        q: 31,
        w: 13,
        x: 25,
        y: 13
    },
    Y: {
        a: 6,
        b: 50,
        c: 6,
        d: 13,
        e: 6,
        f: 31,
        g: 13,
        h: 50,
        i: 44,
        j: 6,
        k: 50,
        l: 50,
        m: 31,
        n: 31,
        o: 6,
        p: 31,
        r: 31,
        s: 0,
        t: 31,
        u: 25,
        v: 13,
        z: 19,
        q: 13,
        w: 13,
        x: 13,
        y: 13
    }
}

/** Font A — Table 9.6 — lowercase → lowercase + symbols ( /, -, ., ) ) */
export const KERN_A_LL: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    a: {
        a: 63,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 56,
        g: 69,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 69,
        m: 75,
        n: 75,
        o: 69,
        p: 75,
        r: 75,
        s: 56,
        t: 56,
        u: 69,
        v: 50,
        z: 56,
        q: 69,
        w: 50,
        x: 50,
        y: 50,
        '/': 50,
        '-': 75,
        '.': 63,
        ')': 56
    },
    b: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 50,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 50,
        u: 69,
        v: 44,
        z: 50,
        q: 63,
        w: 44,
        x: 44,
        y: 44,
        '/': 38,
        '-': 75,
        '.': 50,
        ')': 44
    },
    c: {
        a: 38,
        b: 50,
        c: 38,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 13,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 38,
        t: 31,
        u: 50,
        v: 25,
        z: 31,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 50,
        '.': 31,
        ')': 31
    },
    d: {
        a: 63,
        b: 81,
        c: 63,
        d: 69,
        e: 69,
        f: 69,
        g: 69,
        h: 81,
        i: 81,
        j: 44,
        k: 81,
        l: 81,
        m: 75,
        n: 75,
        o: 69,
        p: 75,
        r: 75,
        s: 63,
        t: 69,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56,
        '/': 50,
        '-': 88,
        '.': 63,
        ')': 56
    },
    e: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 56,
        u: 69,
        v: 28,
        z: 44,
        q: 63,
        w: 28,
        x: 28,
        y: 28,
        '/': 38,
        '-': 75,
        '.': 50,
        ')': 38
    },
    f: {
        a: 38,
        b: 63,
        c: 38,
        d: 44,
        e: 44,
        f: 44,
        g: 44,
        h: 63,
        i: 63,
        j: 25,
        k: 63,
        l: 56,
        m: 56,
        n: 56,
        o: 44,
        p: 56,
        r: 56,
        s: 38,
        t: 44,
        u: 50,
        v: 28,
        z: 44,
        q: 44,
        w: 28,
        x: 28,
        y: 28,
        '/': 6,
        '-': 50,
        '.': 31,
        ')': 38
    },
    g: {
        a: 63,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 75,
        i: 75,
        j: 44,
        k: 75,
        l: 75,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 63,
        t: 63,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56,
        '/': 50,
        '-': 81,
        '.': 63,
        ')': 63
    },
    h: {
        a: 56,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 56,
        g: 69,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 69,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 56,
        t: 56,
        u: 69,
        v: 50,
        z: 56,
        q: 75,
        w: 50,
        x: 50,
        y: 50,
        '/': 50,
        '-': 75,
        '.': 63,
        ')': 56
    },
    i: {
        a: 63,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 69,
        g: 69,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 75,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 56,
        t: 63,
        u: 69,
        v: 50,
        z: 56,
        q: 75,
        w: 50,
        x: 50,
        y: 50,
        '/': 50,
        '-': 88,
        '.': 63,
        ')': 56
    },
    j: {
        a: 63,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 69,
        g: 69,
        h: 75,
        i: 75,
        j: 56,
        k: 75,
        l: 75,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 56,
        t: 63,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56,
        '/': 50,
        '-': 81,
        '.': 63,
        ')': 63
    },
    k: {
        a: 31,
        b: 44,
        c: 31,
        d: 38,
        e: 31,
        f: 38,
        g: 38,
        h: 44,
        i: 44,
        j: 6,
        k: 44,
        l: 44,
        m: 44,
        n: 44,
        o: 44,
        p: 44,
        r: 44,
        s: 25,
        t: 31,
        u: 38,
        v: 25,
        z: 31,
        q: 38,
        w: 25,
        x: 25,
        y: 25,
        '/': 31,
        '-': 44,
        '.': 44,
        ')': 31
    },
    l: {
        a: 38,
        b: 63,
        c: 44,
        d: 50,
        e: 50,
        f: 50,
        g: 50,
        h: 63,
        i: 63,
        j: 31,
        k: 63,
        l: 63,
        m: 56,
        n: 56,
        o: 50,
        p: 56,
        r: 56,
        s: 38,
        t: 38,
        u: 50,
        v: 31,
        z: 44,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 38,
        '-': 56,
        '.': 56,
        ')': 44
    },
    m: {
        a: 56,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 56,
        g: 69,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 69,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 56,
        t: 56,
        u: 69,
        v: 50,
        z: 56,
        q: 75,
        w: 50,
        x: 50,
        y: 50,
        '/': 50,
        '-': 75,
        '.': 63,
        ')': 56
    },
    n: {
        a: 56,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 56,
        g: 69,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 69,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 56,
        t: 56,
        u: 69,
        v: 50,
        z: 56,
        q: 75,
        w: 50,
        x: 50,
        y: 50,
        '/': 50,
        '-': 75,
        '.': 63,
        ')': 56
    },
    o: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 50,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 50,
        u: 69,
        v: 38,
        z: 44,
        q: 63,
        w: 38,
        x: 38,
        y: 38,
        '/': 38,
        '-': 75,
        '.': 50,
        ')': 38
    },
    p: {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 63,
        f: 50,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 63,
        p: 69,
        r: 69,
        s: 50,
        t: 50,
        u: 69,
        v: 44,
        z: 50,
        q: 63,
        w: 44,
        x: 44,
        y: 44,
        '/': 38,
        '-': 75,
        '.': 50,
        ')': 44
    },
    r: {
        a: 25,
        b: 38,
        c: 19,
        d: 25,
        e: 25,
        f: 25,
        g: 25,
        h: 38,
        i: 38,
        j: 6,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 19,
        t: 25,
        u: 38,
        v: 25,
        z: 25,
        q: 25,
        w: 25,
        x: 25,
        y: 25,
        '/': 0,
        '-': 38,
        '.': 0,
        ')': 25
    },
    s: {
        a: 50,
        b: 63,
        c: 50,
        d: 56,
        e: 56,
        f: 44,
        g: 56,
        h: 63,
        i: 63,
        j: 25,
        k: 63,
        l: 56,
        m: 63,
        n: 63,
        o: 56,
        p: 63,
        r: 63,
        s: 44,
        t: 44,
        u: 56,
        v: 38,
        z: 44,
        q: 56,
        w: 38,
        x: 38,
        y: 38,
        '/': 38,
        '-': 75,
        '.': 50,
        ')': 38
    },
    t: {
        a: 50,
        b: 69,
        c: 50,
        d: 56,
        e: 56,
        f: 56,
        g: 56,
        h: 69,
        i: 69,
        j: 38,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 56,
        p: 69,
        r: 69,
        s: 44,
        t: 50,
        u: 56,
        v: 44,
        z: 44,
        q: 56,
        w: 44,
        x: 44,
        y: 44,
        '/': 38,
        '-': 63,
        '.': 56,
        ')': 50
    },
    u: {
        a: 63,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 75,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 63,
        t: 63,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56,
        '/': 50,
        '-': 81,
        '.': 63,
        ')': 56
    },
    v: {
        a: 38,
        b: 56,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 19,
        k: 56,
        l: 50,
        m: 56,
        n: 56,
        o: 38,
        p: 56,
        r: 56,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 31,
        q: 44,
        w: 31,
        x: 31,
        y: 31,
        '/': 0,
        '-': 50,
        '.': 6,
        ')': 31
    },
    z: {
        a: 38,
        b: 56,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 19,
        k: 56,
        l: 50,
        m: 56,
        n: 56,
        o: 38,
        p: 56,
        r: 56,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 31,
        q: 44,
        w: 31,
        x: 31,
        y: 31,
        '/': 31,
        '-': 63,
        '.': 56,
        ')': 50
    },
    q: {
        a: 63,
        b: 75,
        c: 63,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 75,
        i: 75,
        j: 50,
        k: 75,
        l: 75,
        m: 75,
        n: 75,
        o: 75,
        p: 75,
        r: 75,
        s: 63,
        t: 63,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56,
        '/': 50,
        '-': 81,
        '.': 63,
        ')': 63
    },
    w: {
        a: 38,
        b: 56,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 19,
        k: 56,
        l: 50,
        m: 56,
        n: 56,
        o: 38,
        p: 56,
        r: 56,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 31,
        q: 44,
        w: 31,
        x: 31,
        y: 31,
        '/': 6,
        '-': 50,
        '.': 13,
        ')': 31
    },
    x: {
        a: 38,
        b: 56,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 19,
        k: 56,
        l: 50,
        m: 56,
        n: 56,
        o: 38,
        p: 56,
        r: 56,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 31,
        q: 44,
        w: 31,
        x: 31,
        y: 31,
        '/': 31,
        '-': 50,
        '.': 44,
        ')': 38
    },
    y: {
        a: 38,
        b: 56,
        c: 38,
        d: 44,
        e: 38,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 19,
        k: 56,
        l: 50,
        m: 56,
        n: 56,
        o: 38,
        p: 56,
        r: 56,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 31,
        q: 44,
        w: 31,
        x: 31,
        y: 31,
        '/': 0,
        '-': 50,
        '.': 6,
        ')': 31
    },
    '.': {
        a: 69,
        b: 81,
        c: 69,
        d: 69,
        e: 69,
        f: 63,
        g: 69,
        h: 81,
        i: 81,
        j: 50,
        k: 81,
        l: 75,
        m: 81,
        n: 81,
        o: 69,
        p: 81,
        r: 81,
        s: 69,
        t: 56,
        u: 69,
        v: 38,
        z: 75,
        q: 69,
        w: 38,
        x: 69,
        y: 38,
        '/': 69,
        '-': 25
    }
}

/** Font A — Table 9.7 — digit → digit + punctuation ( -, comma, ( ) */
export const KERN_A_DD: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    '1': {
        '1': 88,
        '2': 81,
        '3': 81,
        '4': 75,
        '5': 81,
        '6': 81,
        '7': 81,
        '8': 81,
        '9': 81,
        '0': 81,
        '-': 88,
        ',': 69,
        '%': 106,
        ')': 56
    },
    '2': {
        '1': 69,
        '2': 63,
        '3': 56,
        '4': 63,
        '5': 63,
        '6': 56,
        '7': 63,
        '8': 56,
        '9': 63,
        '0': 63,
        '-': 56,
        ',': 63,
        '%': 94,
        ')': 44
    },
    '3': {
        '1': 63,
        '2': 63,
        '3': 56,
        '4': 56,
        '5': 56,
        '6': 56,
        '7': 63,
        '8': 56,
        '9': 56,
        '0': 63,
        '-': 75,
        ',': 50,
        '%': 94,
        ')': 38
    },
    '4': {
        '1': 63,
        '2': 63,
        '3': 56,
        '4': 50,
        '5': 63,
        '6': 56,
        '7': 50,
        '8': 56,
        '9': 56,
        '0': 63,
        '-': 56,
        ',': 50,
        '%': 94,
        ')': 31
    },
    '5': {
        '1': 75,
        '2': 63,
        '3': 56,
        '4': 63,
        '5': 69,
        '6': 63,
        '7': 63,
        '8': 56,
        '9': 63,
        '0': 63,
        '-': 75,
        ',': 50,
        '%': 100,
        ')': 44
    },
    '6': {
        '1': 56,
        '2': 56,
        '3': 50,
        '4': 56,
        '5': 63,
        '6': 56,
        '7': 56,
        '8': 56,
        '9': 56,
        '0': 63,
        '-': 75,
        ',': 50,
        '%': 94,
        ')': 38
    },
    '7': {
        '1': 69,
        '2': 50,
        '3': 44,
        '4': 25,
        '5': 31,
        '6': 56,
        '7': 44,
        '8': 50,
        '9': 56,
        '0': 56,
        '-': 44,
        ',': 0,
        '%': 75,
        ')': 38
    },
    '8': {
        '1': 63,
        '2': 63,
        '3': 56,
        '4': 56,
        '5': 56,
        '6': 56,
        '7': 63,
        '8': 56,
        '9': 56,
        '0': 63,
        '-': 75,
        ',': 50,
        '%': 94,
        ')': 38
    },
    '9': {
        '1': 63,
        '2': 63,
        '3': 50,
        '4': 31,
        '5': 56,
        '6': 44,
        '7': 56,
        '8': 50,
        '9': 56,
        '0': 56,
        '-': 56,
        ',': 13,
        '%': 88,
        ')': 31
    },
    '0': {
        '1': 69,
        '2': 69,
        '3': 56,
        '4': 69,
        '5': 63,
        '6': 56,
        '7': 63,
        '8': 56,
        '9': 63,
        '0': 69,
        '-': 75,
        ',': 50,
        '%': 94,
        ')': 44
    },
    '-': {
        '1': 56,
        '2': 44,
        '3': 63,
        '4': 69,
        '5': 69,
        '6': 75,
        '7': 50,
        '8': 75,
        '9': 56,
        '0': 75
    },
    ',': {
        '1': 50,
        '2': 69,
        '3': 56,
        '4': 63,
        '5': 56,
        '6': 56,
        '7': 38,
        '8': 56,
        '9': 31,
        '0': 56
    },
    '(': {
        '1': 38,
        '2': 44,
        '3': 38,
        '4': 31,
        '5': 44,
        '6': 38,
        '7': 44,
        '8': 38,
        '9': 31,
        '0': 44
    }
}

/** Font A — Table 9.8 — digit → lowercase letter */
export const KERN_A_DL: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    '1': {
        a: 63,
        b: 81,
        c: 63,
        d: 69,
        e: 63,
        f: 63,
        g: 69,
        h: 81,
        i: 81,
        j: 44,
        k: 81,
        l: 81,
        m: 75,
        n: 75,
        o: 63,
        p: 75,
        r: 81,
        s: 56,
        t: 69,
        u: 75,
        v: 56,
        z: 63,
        q: 69,
        w: 56,
        x: 56,
        y: 56
    },
    '2': {
        a: 50,
        b: 69,
        c: 50,
        d: 56,
        e: 50,
        f: 56,
        g: 56,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 63,
        n: 63,
        o: 50,
        p: 56,
        r: 56,
        s: 44,
        t: 56,
        u: 56,
        v: 44,
        z: 50,
        q: 56,
        w: 44,
        x: 44,
        y: 44
    },
    '3': {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 56,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 56,
        p: 69,
        r: 69,
        s: 50,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    '4': {
        a: 50,
        b: 69,
        c: 50,
        d: 56,
        e: 50,
        f: 56,
        g: 56,
        h: 69,
        i: 69,
        j: 38,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 50,
        p: 69,
        r: 69,
        s: 44,
        t: 50,
        u: 56,
        v: 38,
        z: 44,
        q: 56,
        w: 38,
        x: 44,
        y: 38
    },
    '5': {
        a: 56,
        b: 75,
        c: 56,
        d: 63,
        e: 56,
        f: 63,
        g: 63,
        h: 75,
        i: 75,
        j: 38,
        k: 75,
        l: 75,
        m: 69,
        n: 69,
        o: 56,
        p: 69,
        r: 69,
        s: 50,
        t: 69,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    '6': {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 56,
        f: 50,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 56,
        p: 69,
        r: 69,
        s: 50,
        t: 50,
        u: 69,
        v: 38,
        z: 44,
        q: 63,
        w: 38,
        x: 38,
        y: 38
    },
    '7': {
        a: 19,
        b: 50,
        c: 19,
        d: 25,
        e: 19,
        f: 44,
        g: 25,
        h: 50,
        i: 50,
        j: 13,
        k: 50,
        l: 50,
        m: 44,
        n: 44,
        o: 19,
        p: 38,
        r: 38,
        s: 13,
        t: 44,
        u: 38,
        v: 19,
        z: 25,
        q: 25,
        w: 19,
        x: 19,
        y: 19
    },
    '8': {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 56,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 56,
        p: 69,
        r: 69,
        s: 44,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    },
    '9': {
        a: 38,
        b: 63,
        c: 38,
        d: 44,
        e: 38,
        f: 50,
        g: 44,
        h: 63,
        i: 63,
        j: 19,
        k: 63,
        l: 63,
        m: 56,
        n: 56,
        o: 38,
        p: 50,
        r: 50,
        s: 31,
        t: 50,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    '0': {
        a: 56,
        b: 69,
        c: 56,
        d: 63,
        e: 56,
        f: 63,
        g: 63,
        h: 69,
        i: 69,
        j: 31,
        k: 69,
        l: 69,
        m: 69,
        n: 69,
        o: 56,
        p: 69,
        r: 69,
        s: 50,
        t: 63,
        u: 63,
        v: 50,
        z: 56,
        q: 63,
        w: 50,
        x: 50,
        y: 50
    }
}

/** Font B — Table 9.9 — uppercase → uppercase + symbols ( /, -, ., ( ) */
export const KERN_B_UU: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    A: {
        A: 25,
        B: 38,
        C: 25,
        D: 38,
        E: 38,
        F: 38,
        G: 25,
        H: 38,
        I: 38,
        J: 19,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 25,
        P: 38,
        R: 38,
        S: 19,
        T: -6,
        U: 25,
        V: -6,
        Z: 25,
        Q: 25,
        W: 0,
        X: 25,
        Y: -13,
        '.': 31
    },
    B: {
        A: 25,
        B: 50,
        C: 44,
        D: 50,
        E: 50,
        F: 50,
        G: 44,
        H: 50,
        I: 50,
        J: 19,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 44,
        P: 50,
        R: 50,
        S: 38,
        T: 25,
        U: 44,
        V: 19,
        Z: 31,
        Q: 44,
        W: 25,
        X: 25,
        Y: 19,
        '.': 31
    },
    C: {
        A: 25,
        B: 44,
        C: 38,
        D: 44,
        E: 44,
        F: 44,
        G: 38,
        H: 44,
        I: 44,
        J: 19,
        K: 44,
        L: 44,
        M: 44,
        N: 44,
        O: 38,
        P: 44,
        R: 44,
        S: 31,
        T: 19,
        U: 44,
        V: 19,
        Z: 25,
        Q: 38,
        W: 25,
        X: 19,
        Y: 13,
        '.': 31
    },
    D: {
        A: 25,
        B: 50,
        C: 44,
        D: 50,
        E: 50,
        F: 50,
        G: 44,
        H: 50,
        I: 50,
        J: 19,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 44,
        P: 50,
        R: 50,
        S: 38,
        T: 25,
        U: 44,
        V: 19,
        Z: 31,
        Q: 44,
        W: 25,
        X: 25,
        Y: 19,
        '.': 31
    },
    E: {
        A: 31,
        B: 50,
        C: 38,
        D: 50,
        E: 50,
        F: 50,
        G: 38,
        H: 50,
        I: 50,
        J: 25,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 38,
        P: 50,
        R: 50,
        S: 31,
        T: 31,
        U: 44,
        V: 25,
        Z: 38,
        Q: 38,
        W: 31,
        X: 31,
        Y: 25,
        '.': 38
    },
    F: {
        A: 0,
        B: 38,
        C: 31,
        D: 38,
        E: 38,
        F: 38,
        G: 31,
        H: 38,
        I: 38,
        J: -19,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 31,
        P: 38,
        R: 38,
        S: 31,
        T: 25,
        U: 31,
        V: 25,
        Z: 31,
        Q: 31,
        W: 25,
        X: 25,
        Y: 25,
        '.': -6
    },
    G: {
        A: 25,
        B: 50,
        C: 44,
        D: 50,
        E: 50,
        F: 50,
        G: 44,
        H: 50,
        I: 50,
        J: 19,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 44,
        P: 50,
        R: 50,
        S: 38,
        T: 25,
        U: 44,
        V: 19,
        Z: 31,
        Q: 44,
        W: 25,
        X: 25,
        Y: 19,
        '.': 31
    },
    H: {
        A: 38,
        B: 56,
        C: 50,
        D: 56,
        E: 56,
        F: 56,
        G: 50,
        H: 56,
        I: 56,
        J: 25,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 50,
        P: 56,
        R: 56,
        S: 44,
        T: 31,
        U: 50,
        V: 31,
        Z: 44,
        Q: 50,
        W: 38,
        X: 38,
        Y: 31,
        '.': 44
    },
    I: {
        A: 38,
        B: 56,
        C: 50,
        D: 56,
        E: 56,
        F: 56,
        G: 50,
        H: 56,
        I: 56,
        J: 25,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 50,
        P: 56,
        R: 56,
        S: 44,
        T: 31,
        U: 50,
        V: 31,
        Z: 44,
        Q: 50,
        W: 38,
        X: 38,
        Y: 31,
        '.': 44
    },
    J: {
        A: 25,
        B: 50,
        C: 44,
        D: 50,
        E: 50,
        F: 50,
        G: 44,
        H: 50,
        I: 50,
        J: 19,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 44,
        P: 50,
        R: 50,
        S: 38,
        T: 31,
        U: 50,
        V: 25,
        Z: 38,
        Q: 44,
        W: 31,
        X: 31,
        Y: 25,
        '.': 31
    },
    K: {
        A: 25,
        B: 31,
        C: 19,
        D: 31,
        E: 31,
        F: 31,
        G: 19,
        H: 31,
        I: 31,
        J: 19,
        K: 31,
        L: 31,
        M: 31,
        N: 31,
        O: 19,
        P: 31,
        R: 31,
        S: 13,
        T: 13,
        U: 25,
        V: 13,
        Z: 31,
        Q: 19,
        W: 19,
        X: 25,
        Y: 13,
        '.': 31
    },
    L: {
        A: 25,
        B: 38,
        C: 25,
        D: 38,
        E: 38,
        F: 38,
        G: 25,
        H: 38,
        I: 38,
        J: 25,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 25,
        P: 38,
        R: 38,
        S: 25,
        T: -6,
        U: 25,
        V: -13,
        Z: 31,
        Q: 25,
        W: 0,
        X: 25,
        Y: -13,
        '.': 38
    },
    M: {
        A: 38,
        B: 56,
        C: 50,
        D: 56,
        E: 56,
        F: 56,
        G: 50,
        H: 56,
        I: 56,
        J: 25,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 50,
        P: 56,
        R: 56,
        S: 44,
        T: 31,
        U: 50,
        V: 31,
        Z: 44,
        Q: 50,
        W: 38,
        X: 38,
        Y: 31,
        '.': 44
    },
    N: {
        A: 38,
        B: 56,
        C: 50,
        D: 56,
        E: 56,
        F: 56,
        G: 50,
        H: 56,
        I: 56,
        J: 25,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 50,
        P: 56,
        R: 56,
        S: 44,
        T: 31,
        U: 50,
        V: 31,
        Z: 44,
        Q: 50,
        W: 38,
        X: 38,
        Y: 31,
        '.': 44
    },
    O: {
        A: 25,
        B: 50,
        C: 44,
        D: 50,
        E: 50,
        F: 50,
        G: 44,
        H: 50,
        I: 50,
        J: 19,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 44,
        P: 50,
        R: 50,
        S: 38,
        T: 25,
        U: 44,
        V: 19,
        Z: 31,
        Q: 44,
        W: 25,
        X: 25,
        Y: 19,
        '.': 31
    },
    P: {
        A: -6,
        B: 38,
        C: 38,
        D: 38,
        E: 38,
        F: 38,
        G: 38,
        H: 38,
        I: 38,
        J: -25,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 38,
        P: 38,
        R: 38,
        S: 31,
        T: 19,
        U: 38,
        V: 19,
        Z: 25,
        Q: 38,
        W: 19,
        X: 13,
        Y: 19,
        '.': -6
    },
    R: {
        A: 25,
        B: 38,
        C: 31,
        D: 38,
        E: 38,
        F: 38,
        G: 31,
        H: 38,
        I: 38,
        J: 19,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 31,
        P: 38,
        R: 38,
        S: 25,
        T: 13,
        U: 31,
        V: 13,
        Z: 31,
        Q: 31,
        W: 19,
        X: 25,
        Y: 13,
        '.': 31
    },
    S: {
        A: 19,
        B: 44,
        C: 38,
        D: 44,
        E: 44,
        F: 44,
        G: 38,
        H: 44,
        I: 44,
        J: 19,
        K: 44,
        L: 44,
        M: 44,
        N: 44,
        O: 38,
        P: 44,
        R: 44,
        S: 31,
        T: 19,
        U: 44,
        V: 19,
        Z: 31,
        Q: 38,
        W: 25,
        X: 25,
        Y: 19,
        '.': 31
    },
    T: {
        A: -6,
        B: 31,
        C: 25,
        D: 31,
        E: 31,
        F: 31,
        G: 25,
        H: 31,
        I: 31,
        J: -19,
        K: 31,
        L: 31,
        M: 31,
        N: 31,
        O: 25,
        P: 31,
        R: 31,
        S: 19,
        T: 25,
        U: 31,
        V: 19,
        Z: 25,
        Q: 25,
        W: 25,
        X: 25,
        Y: 19,
        '.': 6
    },
    U: {
        A: 25,
        B: 50,
        C: 44,
        D: 50,
        E: 50,
        F: 50,
        G: 44,
        H: 50,
        I: 50,
        J: 19,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 44,
        P: 50,
        R: 50,
        S: 38,
        T: 31,
        U: 50,
        V: 25,
        Z: 38,
        Q: 44,
        W: 31,
        X: 31,
        Y: 25,
        '.': 31
    },
    V: {
        A: -6,
        B: 31,
        C: 19,
        D: 31,
        E: 31,
        F: 31,
        G: 19,
        H: 31,
        I: 31,
        J: -19,
        K: 31,
        L: 31,
        M: 31,
        N: 31,
        O: 19,
        P: 31,
        R: 31,
        S: 13,
        T: 19,
        U: 25,
        V: 19,
        Z: 25,
        Q: 19,
        W: 19,
        X: 19,
        Y: 19,
        '.': 6
    },
    Z: {
        A: 25,
        B: 44,
        C: 31,
        D: 44,
        E: 44,
        F: 44,
        G: 31,
        H: 44,
        I: 44,
        J: 25,
        K: 44,
        L: 44,
        M: 44,
        N: 44,
        O: 31,
        P: 44,
        R: 44,
        S: 25,
        T: 31,
        U: 38,
        V: 31,
        Z: 38,
        Q: 31,
        W: 31,
        X: 31,
        Y: 31,
        '.': 38
    },
    Q: {
        A: 19,
        B: 31,
        C: 25,
        D: 31,
        E: 31,
        F: 31,
        G: 25,
        H: 31,
        I: 31,
        J: 19,
        K: 31,
        L: 31,
        M: 31,
        N: 31,
        O: 25,
        P: 31,
        R: 31,
        S: 25,
        T: 0,
        U: 25,
        V: 6,
        Z: 31,
        Q: 25,
        W: 6,
        X: 25,
        Y: 0,
        '.': 31
    },
    W: {
        A: 0,
        B: 38,
        C: 25,
        D: 38,
        E: 38,
        F: 38,
        G: 25,
        H: 38,
        I: 38,
        J: -6,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 25,
        P: 38,
        R: 38,
        S: 19,
        T: 25,
        U: 31,
        V: 19,
        Z: 31,
        Q: 25,
        W: 19,
        X: 19,
        Y: 25,
        '.': 6
    },
    X: {
        A: 25,
        B: 38,
        C: 25,
        D: 38,
        E: 38,
        F: 38,
        G: 25,
        H: 38,
        I: 38,
        J: 19,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 25,
        P: 38,
        R: 38,
        S: 19,
        T: 25,
        U: 31,
        V: 19,
        Z: 38,
        Q: 25,
        W: 19,
        X: 25,
        Y: 19,
        '.': 31
    },
    Y: {
        A: -13,
        B: 31,
        C: 19,
        D: 31,
        E: 31,
        F: 31,
        G: 19,
        H: 31,
        I: 31,
        J: -25,
        K: 31,
        L: 31,
        M: 31,
        N: 31,
        O: 19,
        P: 31,
        R: 31,
        S: 19,
        T: 19,
        U: 25,
        V: 19,
        Z: 25,
        Q: 19,
        W: 25,
        X: 19,
        Y: 19,
        '.': -13
    },
    '/': {
        A: 0,
        B: 38,
        C: 25,
        D: 38,
        E: 38,
        F: 38,
        G: 25,
        H: 38,
        I: 38,
        J: -13,
        K: 38,
        L: 38,
        M: 38,
        N: 38,
        O: 25,
        P: 38,
        R: 38,
        S: 19,
        T: 31,
        U: 38,
        V: 31,
        Z: 31,
        Q: 25,
        W: 31,
        X: 25,
        Y: 31
    },
    '-': {
        A: 31,
        B: 63,
        C: 50,
        D: 63,
        E: 63,
        F: 63,
        G: 50,
        H: 63,
        I: 63,
        J: 13,
        K: 63,
        L: 63,
        M: 63,
        N: 63,
        O: 63,
        P: 63,
        R: 63,
        S: 50,
        T: 19,
        U: 50,
        V: 25,
        Z: 44,
        Q: 50,
        W: 25,
        X: 25,
        Y: 25
    },
    '.': {
        A: 44,
        B: 56,
        C: 44,
        D: 56,
        E: 56,
        F: 56,
        G: 44,
        H: 56,
        I: 56,
        J: 44,
        K: 56,
        L: 56,
        M: 56,
        N: 56,
        O: 44,
        P: 56,
        R: 56,
        S: 44,
        T: 19,
        U: 44,
        V: 25,
        Z: 50,
        Q: 44,
        W: 25,
        X: 44,
        Y: 25
    },
    '(': {
        A: 38,
        B: 50,
        C: 38,
        D: 50,
        E: 50,
        F: 50,
        G: 38,
        H: 50,
        I: 50,
        J: 31,
        K: 50,
        L: 50,
        M: 50,
        N: 50,
        O: 38,
        P: 50,
        R: 50,
        S: 31,
        T: 38,
        U: 44,
        V: 31,
        Z: 44,
        Q: 38,
        W: 31,
        X: 38,
        Y: 31
    }
}

/** Font B — Table 9.10 — uppercase → lowercase */
export const KERN_B_UL: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    A: {
        a: 25,
        b: 38,
        c: 25,
        d: 31,
        e: 25,
        f: 13,
        g: 31,
        h: 38,
        i: 38,
        j: 19,
        k: 38,
        l: 31,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 19,
        t: 6,
        u: 31,
        v: 6,
        z: 25,
        q: 31,
        w: 6,
        x: 19,
        y: 6
    },
    B: {
        a: 38,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 25,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    C: {
        a: 25,
        b: 44,
        c: 31,
        d: 31,
        e: 31,
        f: 25,
        g: 31,
        h: 44,
        i: 44,
        j: 19,
        k: 44,
        l: 44,
        m: 44,
        n: 44,
        o: 31,
        p: 44,
        r: 44,
        s: 25,
        t: 25,
        u: 38,
        v: 19,
        z: 25,
        q: 31,
        w: 19,
        x: 19,
        y: 19
    },
    D: {
        a: 38,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 25,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    E: {
        a: 38,
        b: 50,
        c: 38,
        d: 38,
        e: 38,
        f: 25,
        g: 38,
        h: 50,
        i: 44,
        j: 19,
        k: 50,
        l: 44,
        m: 44,
        n: 44,
        o: 38,
        p: 44,
        r: 44,
        s: 31,
        t: 31,
        u: 44,
        v: 19,
        z: 31,
        q: 38,
        w: 19,
        x: 25,
        y: 19
    },
    F: {
        a: 25,
        b: 38,
        c: 25,
        d: 25,
        e: 25,
        f: 19,
        g: 25,
        h: 38,
        i: 38,
        j: 6,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 19,
        t: 19,
        u: 38,
        v: 19,
        z: 25,
        q: 25,
        w: 19,
        x: 19,
        y: 19
    },
    G: {
        a: 38,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 25,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    H: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 50,
        s: 38,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31
    },
    I: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 50,
        s: 38,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31
    },
    J: {
        a: 44,
        b: 56,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 31,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 38,
        u: 44,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    K: {
        a: 19,
        b: 31,
        c: 19,
        d: 25,
        e: 19,
        f: 13,
        g: 25,
        h: 31,
        i: 31,
        j: 13,
        k: 31,
        l: 25,
        m: 25,
        n: 25,
        o: 19,
        p: 25,
        r: 25,
        s: 13,
        t: 13,
        u: 25,
        v: 6,
        z: 19,
        q: 25,
        w: 6,
        x: 13,
        y: 6
    },
    L: {
        a: 25,
        b: 38,
        c: 25,
        d: 25,
        e: 25,
        f: 13,
        g: 25,
        h: 38,
        i: 38,
        j: 25,
        k: 38,
        l: 31,
        m: 31,
        n: 31,
        o: 25,
        p: 31,
        r: 31,
        s: 25,
        t: -6,
        u: 25,
        v: -13,
        z: 31,
        q: 25,
        w: 0,
        x: 25,
        y: -13
    },
    M: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 50,
        s: 38,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31
    },
    N: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 50,
        s: 38,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31
    },
    O: {
        a: 38,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 25,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    P: {
        a: 25,
        b: 38,
        c: 25,
        d: 25,
        e: 25,
        f: 25,
        g: 25,
        h: 38,
        i: 38,
        j: 6,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 19,
        t: 25,
        u: 38,
        v: 25,
        z: 25,
        q: 25,
        w: 25,
        x: 25,
        y: 25
    },
    R: {
        a: 19,
        b: 38,
        c: 25,
        d: 25,
        e: 25,
        f: 19,
        g: 25,
        h: 38,
        i: 38,
        j: 19,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 19,
        t: 19,
        u: 31,
        v: 13,
        z: 25,
        q: 25,
        w: 13,
        x: 19,
        y: 13
    },
    S: {
        a: 38,
        b: 44,
        c: 38,
        d: 38,
        e: 38,
        f: 25,
        g: 38,
        h: 44,
        i: 44,
        j: 19,
        k: 44,
        l: 44,
        m: 44,
        n: 44,
        o: 38,
        p: 44,
        r: 44,
        s: 25,
        t: 31,
        u: 44,
        v: 19,
        z: 31,
        q: 38,
        w: 19,
        x: 19,
        y: 19
    },
    T: {
        a: 0,
        b: 31,
        c: 0,
        d: 6,
        e: 0,
        f: 13,
        g: 6,
        h: 31,
        i: 31,
        j: 0,
        k: 31,
        l: 31,
        m: 19,
        n: 19,
        o: 0,
        p: 19,
        r: 19,
        s: 0,
        t: 13,
        u: 13,
        v: 0,
        z: 6,
        q: 6,
        w: 0,
        x: 0,
        y: 0
    },
    U: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 31,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 38,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    V: {
        a: 13,
        b: 25,
        c: 13,
        d: 13,
        e: 13,
        f: 13,
        g: 13,
        h: 25,
        i: 25,
        j: 0,
        k: 25,
        l: 25,
        m: 19,
        n: 19,
        o: 13,
        p: 19,
        r: 19,
        s: 6,
        t: 13,
        u: 19,
        v: 13,
        z: 13,
        q: 13,
        w: 13,
        x: 13,
        y: 13
    },
    Z: {
        a: 31,
        b: 38,
        c: 31,
        d: 31,
        e: 31,
        f: 19,
        g: 31,
        h: 38,
        i: 38,
        j: 13,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 31,
        p: 38,
        r: 38,
        s: 19,
        t: 31,
        u: 38,
        v: 13,
        z: 31,
        q: 31,
        w: 13,
        x: 19,
        y: 13
    },
    Q: {
        a: 19,
        b: 31,
        c: 19,
        d: 19,
        e: 19,
        f: 13,
        g: 19,
        h: 31,
        i: 31,
        j: 19,
        k: 31,
        l: 31,
        m: 31,
        n: 31,
        o: 19,
        p: 31,
        r: 31,
        s: 13,
        t: 19,
        u: 25,
        v: 6,
        z: 19,
        q: 19,
        w: 6,
        x: 13,
        y: 6
    },
    W: {
        a: 19,
        b: 31,
        c: 19,
        d: 19,
        e: 19,
        f: 19,
        g: 19,
        h: 31,
        i: 31,
        j: 6,
        k: 31,
        l: 31,
        m: 25,
        n: 25,
        o: 19,
        p: 25,
        r: 25,
        s: 13,
        t: 19,
        u: 25,
        v: 19,
        z: 19,
        q: 19,
        w: 19,
        x: 19,
        y: 19
    },
    X: {
        a: 19,
        b: 31,
        c: 19,
        d: 25,
        e: 19,
        f: 13,
        g: 25,
        h: 31,
        i: 31,
        j: 13,
        k: 31,
        l: 25,
        m: 25,
        n: 25,
        o: 19,
        p: 25,
        r: 25,
        s: 13,
        t: 13,
        u: 25,
        v: 6,
        z: 19,
        q: 25,
        w: 6,
        x: 13,
        y: 6
    },
    Y: {
        a: 0,
        b: 25,
        c: 0,
        d: 0,
        e: 0,
        f: 6,
        g: 0,
        h: 25,
        i: 25,
        j: 0,
        k: 25,
        l: 25,
        m: 13,
        n: 13,
        o: 0,
        p: 13,
        r: 13,
        s: -6,
        t: 6,
        u: 13,
        v: 6,
        z: 6,
        q: 0,
        w: 6,
        x: 6,
        y: 6
    }
}

/** Font B — Table 9.11 — lowercase → lowercase + symbols ( /, -, ., ) ) */
export const KERN_B_LL: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    a: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 50,
        v: 25,
        z: 31,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 44,
        '-': 50,
        '.': 44,
        ')': 38
    },
    b: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 19,
        k: 50,
        l: 44,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 44,
        v: 25,
        z: 25,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 50,
        '.': 31,
        ')': 31
    },
    c: {
        a: 25,
        b: 31,
        c: 31,
        d: 31,
        e: 31,
        f: 25,
        g: 31,
        h: 31,
        i: 31,
        j: 6,
        k: 31,
        l: 31,
        m: 31,
        n: 31,
        o: 31,
        p: 31,
        r: 31,
        s: 19,
        t: 25,
        u: 31,
        v: 19,
        z: 19,
        q: 31,
        w: 19,
        x: 19,
        y: 19,
        '/': 19,
        '-': 31,
        '.': 31,
        ')': 19
    },
    d: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 38,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 44,
        '-': 63,
        '.': 44,
        ')': 38
    },
    e: {
        a: 38,
        b: 44,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 44,
        i: 44,
        j: 19,
        k: 44,
        l: 44,
        m: 44,
        n: 44,
        o: 44,
        p: 44,
        r: 44,
        s: 31,
        t: 25,
        u: 44,
        v: 25,
        z: 31,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 50,
        '.': 31,
        ')': 31
    },
    f: {
        a: 25,
        b: 38,
        c: 25,
        d: 31,
        e: 25,
        f: 25,
        g: 31,
        h: 38,
        i: 38,
        j: 13,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 25,
        p: 38,
        r: 38,
        s: 25,
        t: 31,
        u: 38,
        v: 19,
        z: 31,
        q: 38,
        w: 19,
        x: 19,
        y: 19,
        '/': 6,
        '-': 31,
        '.': 19,
        ')': 38
    },
    g: {
        a: 44,
        b: 50,
        c: 44,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 50,
        i: 50,
        j: 31,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 44,
        '-': 56,
        '.': 44,
        ')': 44
    },
    h: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 38,
        t: 31,
        u: 44,
        v: 25,
        z: 25,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 44,
        '-': 50,
        '.': 44,
        ')': 38
    },
    i: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 25,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 38,
        t: 44,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 44,
        '-': 63,
        '.': 44,
        ')': 38
    },
    j: {
        a: 44,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 44,
        '-': 56,
        '.': 44,
        ')': 44
    },
    k: {
        a: 13,
        b: 25,
        c: 19,
        d: 19,
        e: 19,
        f: 13,
        g: 19,
        h: 25,
        i: 25,
        j: 6,
        k: 25,
        l: 19,
        m: 25,
        n: 25,
        o: 19,
        p: 25,
        r: 25,
        s: 13,
        t: 13,
        u: 19,
        v: 6,
        z: 13,
        q: 19,
        w: 6,
        x: 6,
        y: 6,
        '/': 31,
        '-': 25,
        '.': 31,
        ')': 25
    },
    l: {
        a: 31,
        b: 44,
        c: 31,
        d: 38,
        e: 31,
        f: 19,
        g: 38,
        h: 44,
        i: 44,
        j: 19,
        k: 44,
        l: 38,
        m: 38,
        n: 38,
        o: 31,
        p: 38,
        r: 38,
        s: 25,
        t: 19,
        u: 38,
        v: 19,
        z: 19,
        q: 38,
        w: 19,
        x: 19,
        y: 19,
        '/': 38,
        '-': 38,
        '.': 38,
        ')': 31
    },
    m: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 38,
        t: 31,
        u: 44,
        v: 25,
        z: 25,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 44,
        '-': 56,
        '.': 44,
        ')': 38
    },
    n: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 38,
        t: 31,
        u: 44,
        v: 25,
        z: 25,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 44,
        '-': 56,
        '.': 44,
        ')': 38
    },
    o: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 44,
        v: 25,
        z: 25,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 50,
        '.': 31,
        ')': 31
    },
    p: {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 31,
        g: 44,
        h: 50,
        i: 50,
        j: 19,
        k: 50,
        l: 44,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 31,
        u: 44,
        v: 25,
        z: 25,
        q: 44,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 50,
        '.': 31,
        ')': 31
    },
    r: {
        a: 19,
        b: 31,
        c: 19,
        d: 25,
        e: 19,
        f: 25,
        g: 25,
        h: 31,
        i: 25,
        j: 6,
        k: 31,
        l: 25,
        m: 31,
        n: 31,
        o: 19,
        p: 31,
        r: 31,
        s: 19,
        t: 19,
        u: 25,
        v: 19,
        z: 25,
        q: 25,
        w: 19,
        x: 19,
        y: 19,
        '/': 13,
        '-': 25,
        '.': 0,
        ')': 25
    },
    s: {
        a: 31,
        b: 38,
        c: 31,
        d: 31,
        e: 31,
        f: 25,
        g: 31,
        h: 38,
        i: 38,
        j: 19,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 31,
        p: 38,
        r: 38,
        s: 25,
        t: 19,
        u: 38,
        v: 19,
        z: 19,
        q: 38,
        w: 19,
        x: 19,
        y: 19,
        '/': 38,
        '-': 50,
        '.': 31,
        ')': 31
    },
    t: {
        a: 31,
        b: 44,
        c: 31,
        d: 38,
        e: 31,
        f: 38,
        g: 38,
        h: 44,
        i: 44,
        j: 25,
        k: 44,
        l: 38,
        m: 44,
        n: 44,
        o: 31,
        p: 44,
        r: 44,
        s: 19,
        t: 25,
        u: 38,
        v: 25,
        z: 25,
        q: 38,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 44,
        '.': 38,
        ')': 38
    },
    u: {
        a: 44,
        b: 50,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 50,
        s: 38,
        t: 38,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 44,
        '-': 56,
        '.': 44,
        ')': 38
    },
    v: {
        a: 19,
        b: 31,
        c: 25,
        d: 25,
        e: 25,
        f: 19,
        g: 25,
        h: 31,
        i: 31,
        j: 6,
        k: 31,
        l: 31,
        m: 31,
        n: 31,
        o: 25,
        p: 31,
        r: 31,
        s: 19,
        t: 25,
        u: 31,
        v: 19,
        z: 19,
        q: 25,
        w: 19,
        x: 19,
        y: 19,
        '/': 6,
        '-': 31,
        '.': 6,
        ')': 25
    },
    z: {
        a: 25,
        b: 38,
        c: 31,
        d: 31,
        e: 31,
        f: 25,
        g: 31,
        h: 38,
        i: 38,
        j: 13,
        k: 38,
        l: 38,
        m: 38,
        n: 38,
        o: 31,
        p: 38,
        r: 38,
        s: 25,
        t: 25,
        u: 38,
        v: 25,
        z: 25,
        q: 38,
        w: 25,
        x: 25,
        y: 25,
        '/': 38,
        '-': 44,
        '.': 38,
        ')': 31
    },
    q: {
        a: 44,
        b: 50,
        c: 44,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 50,
        i: 50,
        j: 31,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 31,
        t: 38,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31,
        '/': 44,
        '-': 56,
        '.': 44,
        ')': 44
    },
    w: {
        a: 19,
        b: 31,
        c: 25,
        d: 25,
        e: 25,
        f: 19,
        g: 25,
        h: 31,
        i: 31,
        j: 6,
        k: 31,
        l: 31,
        m: 25,
        n: 25,
        o: 25,
        p: 25,
        r: 25,
        s: 13,
        t: 19,
        u: 25,
        v: 19,
        z: 19,
        q: 25,
        w: 19,
        x: 19,
        y: 19,
        '/': 13,
        '-': 31,
        '.': 6,
        ')': 25
    },
    x: {
        a: 19,
        b: 31,
        c: 25,
        d: 25,
        e: 25,
        f: 19,
        g: 25,
        h: 31,
        i: 31,
        j: 6,
        k: 31,
        l: 31,
        m: 25,
        n: 25,
        o: 25,
        p: 25,
        r: 25,
        s: 13,
        t: 13,
        u: 25,
        v: 6,
        z: 19,
        q: 25,
        w: 6,
        x: 13,
        y: 6,
        '/': 31,
        '-': 31,
        '.': 31,
        ')': 31
    },
    y: {
        a: 19,
        b: 31,
        c: 25,
        d: 25,
        e: 25,
        f: 19,
        g: 25,
        h: 31,
        i: 31,
        j: 6,
        k: 31,
        l: 31,
        m: 31,
        n: 31,
        o: 25,
        p: 31,
        r: 31,
        s: 19,
        t: 25,
        u: 31,
        v: 19,
        z: 19,
        q: 25,
        w: 19,
        x: 19,
        y: 19,
        '/': 6,
        '-': 31,
        '.': 6,
        ')': 25
    },
    '.': {
        a: 44,
        b: 56,
        c: 44,
        d: 44,
        e: 44,
        f: 44,
        g: 44,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 50,
        m: 56,
        n: 56,
        o: 44,
        p: 56,
        r: 56,
        s: 44,
        t: 38,
        u: 44,
        v: 25,
        z: 50,
        q: 44,
        w: 25,
        x: 44,
        y: 25,
        '/': 50,
        '-': 19
    }
}

/** Font B — Table 9.12 — digit → digit + punctuation ( -, comma, ) ) */
export const KERN_B_DD: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    '1': {
        '1': 69,
        '2': 63,
        '3': 63,
        '4': 56,
        '5': 63,
        '6': 63,
        '7': 63,
        '8': 63,
        '9': 63,
        '0': 63,
        '-': 63,
        ',': 44,
        '%': 81,
        ')': 56
    },
    '2': {
        '1': 50,
        '2': 50,
        '3': 44,
        '4': 44,
        '5': 44,
        '6': 44,
        '7': 50,
        '8': 50,
        '9': 50,
        '0': 50,
        '-': 44,
        ',': 44,
        '%': 63,
        ')': 50
    },
    '3': {
        '1': 50,
        '2': 44,
        '3': 44,
        '4': 44,
        '5': 50,
        '6': 50,
        '7': 50,
        '8': 50,
        '9': 50,
        '0': 50,
        '-': 56,
        ',': 31,
        '%': 75,
        ')': 44
    },
    '4': {
        '1': 31,
        '2': 44,
        '3': 44,
        '4': 38,
        '5': 44,
        '6': 44,
        '7': 38,
        '8': 44,
        '9': 44,
        '0': 44,
        '-': 44,
        ',': 38,
        '%': 69,
        ')': 44
    },
    '5': {
        '1': 50,
        '2': 44,
        '3': 44,
        '4': 44,
        '5': 50,
        '6': 50,
        '7': 50,
        '8': 50,
        '9': 44,
        '0': 50,
        '-': 56,
        ',': 31,
        '%': 75,
        ')': 44
    },
    '6': {
        '1': 44,
        '2': 44,
        '3': 44,
        '4': 44,
        '5': 50,
        '6': 50,
        '7': 44,
        '8': 50,
        '9': 44,
        '0': 50,
        '-': 56,
        ',': 31,
        '%': 75,
        ')': 44
    },
    '7': {
        '1': 44,
        '2': 38,
        '3': 38,
        '4': 19,
        '5': 44,
        '6': 44,
        '7': 44,
        '8': 38,
        '9': 31,
        '0': 38,
        '-': 31,
        ',': 0,
        '%': 63,
        ')': 38
    },
    '8': {
        '1': 50,
        '2': 44,
        '3': 44,
        '4': 44,
        '5': 50,
        '6': 50,
        '7': 50,
        '8': 50,
        '9': 50,
        '0': 50,
        '-': 56,
        ',': 31,
        '%': 75,
        ')': 44
    },
    '9': {
        '1': 50,
        '2': 44,
        '3': 44,
        '4': 31,
        '5': 50,
        '6': 38,
        '7': 44,
        '8': 50,
        '9': 44,
        '0': 50,
        '-': 44,
        ',': 6,
        '%': 69,
        ')': 38
    },
    '0': {
        '1': 50,
        '2': 44,
        '3': 44,
        '4': 44,
        '5': 50,
        '6': 50,
        '7': 50,
        '8': 50,
        '9': 44,
        '0': 50,
        '-': 56,
        ',': 31,
        '%': 75,
        ')': 44
    },
    '-': {
        '1': 38,
        '2': 44,
        '3': 44,
        '4': 44,
        '5': 50,
        '6': 50,
        '7': 56,
        '8': 44,
        '9': 56,
        '0': 44
    },
    ',': {
        '1': 25,
        '2': 44,
        '3': 38,
        '4': 44,
        '5': 38,
        '6': 38,
        '7': 25,
        '8': 38,
        '9': 19,
        '0': 38
    },
    ')': {
        '1': 38,
        '2': 50,
        '3': 44,
        '4': 44,
        '5': 44,
        '6': 44,
        '7': 44,
        '8': 44,
        '9': 44,
        '0': 44
    }
}

/** Font B — Table 9.13 — digit → lowercase letter */
export const KERN_B_DL: Readonly<Record<string, Readonly<Record<string, number>>>> = {
    '1': {
        a: 56,
        b: 63,
        c: 56,
        d: 56,
        e: 56,
        f: 44,
        g: 56,
        h: 63,
        i: 63,
        j: 38,
        k: 63,
        l: 63,
        m: 56,
        n: 56,
        o: 56,
        p: 56,
        r: 56,
        s: 56,
        t: 50,
        u: 56,
        v: 38,
        z: 44,
        q: 56,
        w: 38,
        x: 38,
        y: 38
    },
    '2': {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 38,
        g: 44,
        h: 50,
        i: 50,
        j: 25,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 44,
        t: 38,
        u: 50,
        v: 31,
        z: 44,
        q: 44,
        w: 31,
        x: 38,
        y: 31
    },
    '3': {
        a: 50,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 56,
        s: 50,
        t: 44,
        u: 56,
        v: 38,
        z: 44,
        q: 50,
        w: 38,
        x: 38,
        y: 38
    },
    '4': {
        a: 44,
        b: 50,
        c: 44,
        d: 44,
        e: 44,
        f: 38,
        g: 44,
        h: 50,
        i: 50,
        j: 31,
        k: 50,
        l: 50,
        m: 50,
        n: 50,
        o: 44,
        p: 50,
        r: 50,
        s: 44,
        t: 31,
        u: 44,
        v: 31,
        z: 44,
        q: 44,
        w: 31,
        x: 31,
        y: 31
    },
    '5': {
        a: 50,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 38,
        k: 56,
        l: 56,
        m: 50,
        n: 50,
        o: 50,
        p: 50,
        r: 56,
        s: 50,
        t: 44,
        u: 56,
        v: 38,
        z: 44,
        q: 50,
        w: 38,
        x: 38,
        y: 38
    },
    '6': {
        a: 50,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 25,
        k: 56,
        l: 56,
        m: 56,
        n: 56,
        o: 50,
        p: 56,
        r: 56,
        s: 50,
        t: 38,
        u: 50,
        v: 31,
        z: 38,
        q: 50,
        w: 31,
        x: 31,
        y: 31
    },
    '7': {
        a: 19,
        b: 44,
        c: 19,
        d: 25,
        e: 19,
        f: 31,
        g: 25,
        h: 44,
        i: 44,
        j: 19,
        k: 44,
        l: 44,
        m: 31,
        n: 31,
        o: 19,
        p: 31,
        r: 31,
        s: 31,
        t: 31,
        u: 31,
        v: 19,
        z: 25,
        q: 25,
        w: 19,
        x: 19,
        y: 19
    },
    '8': {
        a: 50,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 56,
        n: 56,
        o: 50,
        p: 56,
        r: 56,
        s: 50,
        t: 44,
        u: 56,
        v: 38,
        z: 44,
        q: 50,
        w: 38,
        x: 38,
        y: 38
    },
    '9': {
        a: 38,
        b: 44,
        c: 38,
        d: 38,
        e: 38,
        f: 31,
        g: 44,
        h: 44,
        i: 44,
        j: 25,
        k: 44,
        l: 44,
        m: 38,
        n: 38,
        o: 38,
        p: 38,
        r: 44,
        s: 38,
        t: 31,
        u: 44,
        v: 31,
        z: 38,
        q: 38,
        w: 31,
        x: 31,
        y: 31
    },
    '0': {
        a: 50,
        b: 56,
        c: 50,
        d: 50,
        e: 50,
        f: 38,
        g: 50,
        h: 56,
        i: 56,
        j: 31,
        k: 56,
        l: 56,
        m: 56,
        n: 56,
        o: 50,
        p: 56,
        r: 56,
        s: 50,
        t: 44,
        u: 56,
        v: 38,
        z: 44,
        q: 50,
        w: 38,
        x: 38,
        y: 38
    }
}

/**
 * Special-case digit→unit-abbreviation overrides per the notes that follow
 * Tables 9.7 and 9.12 in the standard.
 *
 * For digit→m (metres) and digit→km (kilometres), the spacing is fixed
 * (overriding the table value entirely).
 *
 * For digit→t (tonnes), the spacing is the regular table value plus a bonus.
 */
export const DIGIT_UNIT_A = {m: 48, km: 48, t_bonus: 8} as const
export const DIGIT_UNIT_B = {m: 32, km: 32, t_bonus: 6} as const
